import React, {
  useMemo,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { VariableSizeList as List } from "react-window";
import Export from "../../../../components/ExportBreakdown";
import { Button } from "nq-component";
import getCustomerCategories from "../customer-count/CustomerCategories";
import LoadingState from "../../../../components/LoadingState";
import getCustomerCategoriesCount from "../customer-count/CustomerCategoriesCount";

function CustomerBreakdownPage({ transactions }) {
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedStore, setSelectedStore] = useState("All");
  const [selectedYear, setSelectedYear] = useState("All");
  const [nameSearch, setNameSearch] = useState("");
  const [numberSearch, setNumberSearch] = useState("");
  const [expandedTransactions, setExpandedTransactions] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalCustomer, setModalCustomer] = useState(null);
  const listRef = useRef(null);

  const getUniqueStores = (transactions) => {
    const storeSet = new Set();
    transactions.forEach((transaction) => {
      const storeCode = transaction.store_code?.replace("ANGELSPIZZA_", "");
      storeSet.add(storeCode);
    });
    return Array.from(storeSet);
  };

  const getUniqueYears = (transactions) => {
    const yearSet = new Set();

    transactions.forEach((customer) => {
      (customer.transactions || []).forEach((tran) => {
        const date = new Date(tran.tran_date || tran.date);
        if (!isNaN(date)) {
          yearSet.add(date.getFullYear());
        }
      });
    });

    return Array.from(yearSet).sort((a, b) => b - a); // descending
  };

  const uniqueYears = useMemo(
    () => getUniqueYears(transactions),
    [transactions]
  );

  const uniqueStores = useMemo(
    () => getUniqueStores(transactions),
    [transactions]
  );

  const filteredCustomers = useMemo(() => {
    const allCustomerCategories = getCustomerCategoriesCount(
      transactions,
      selectedStore
    );

    // Flatten all categories
    // const flattened = Object.values(allCustomerCategories).flat();
    // Attach RFM category to each customer
    const flattened = Object.entries(allCustomerCategories).flatMap(
      ([category, customers]) =>
        customers.map((customer) => ({
          ...customer,
          rfmCategory: category, // ➕ add this
        }))
    );

    const filtered = flattened.filter((customer) => {
      const matchesName = nameSearch
        ? customer.custName?.toLowerCase().includes(nameSearch.toLowerCase())
        : true;

      const matchesNumber = numberSearch
        ? customer.custMobile?.includes(numberSearch)
        : true;

      const matchesYear =
        selectedYear === "All"
          ? true
          : customer.transactions?.some((t) => {
              const date = new Date(t.date);
              return (
                !isNaN(date) && date.getFullYear().toString() === selectedYear
              );
            });

      // Only filter category if it's not "All"
      const matchesCategory =
        selectedFilter === "All"
          ? true
          : (allCustomerCategories[selectedFilter] || []).some(
              (c) =>
                c.custPhone === customer.custPhone &&
                c.custName === customer.custName
            );

      return matchesName && matchesNumber && matchesYear && matchesCategory;
    });

    return filtered;
  }, [
    transactions,
    selectedStore,
    selectedFilter,
    nameSearch,
    numberSearch,
    selectedYear,
  ]);

  console.log("filtered", filteredCustomers);

  const exportCsv = () => {
    const header =
      "Customer,Email,Mobile Number,Phone Number,Address,Barangay,Birthday,Customer Card,Notes,LandMark,Transaction Count,Total Sales,Last Transaction,Day Last Transaction,RFM Type\n";
    const rows = filteredCustomers
      .map(
        (customer) =>
          `"${customer.custName}","${customer.custEmail}","${
            customer.custMobile
          }","${customer.custPhone}","${customer.address}","${
            customer.barangay
          }","${customer.birthday}","${customer.card}","${customer.notes}","${
            customer.landmark
          }","${customer.purchaseCount}","${customer.totalSpend}","${new Date(
            customer.lastPurchaseDate
          ).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}","${customer.daysSinceLastPurchase}","${customer.rfmCategory}"`
      )
      .join("\n");

    const csv = header + rows;

    // Create a Blob from the CSV string
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    // Create a downloadable link
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "customers_breakdown.csv");

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportTransactionsToCsv = (customer) => {
    const header = `Customer Name,Transaction Count,Average Sales,Last Transaction Date,Number of Days Since Last Transaction,RFM Type\n`;
    const base = `"${customer.custName}",${customer.purchaseCount},${(
      customer.totalSpend / customer.purchaseCount
    ).toFixed(2)},"${customer.lastPurchaseDate.toLocaleDateString()}",${
      customer.daysSinceLastPurchase
    },"${selectedFilter}"\n`;
    const transactionHeader = `\nTransaction Date,Spend Total,Store\n`;
    const transactions = customer.transactions
      .map((t) => `"${t.date}",${t.totalSpend.toFixed(2)},${t.storeSpend}`)
      .join("\n");

    const csv = header + base + transactionHeader + transactions;
    const encoded = encodeURI("data:text/csv;charset=utf-8," + csv);
    const link = document.createElement("a");
    link.setAttribute("href", encoded);
    link.setAttribute("download", "customer_transactions.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getItemSize = useCallback(
    (index) => {
      const base = 70;
      const isExpanded = selectedItem === index;
      const customer = filteredCustomers[index];
      const showTransactions = expandedTransactions[index];
      const txCount = showTransactions
        ? customer?.transactions?.length || 0
        : 0;

      const cardHeight = 120; // estimated height per transaction card
      const expandedContent = isExpanded ? 150 : 0; // base for expanded summary/buttons

      return base + expandedContent + txCount * cardHeight;
    },
    [selectedItem, expandedTransactions, filteredCustomers]
  );

  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, true);
    }
  }, [selectedItem, expandedTransactions]);

  const Row = ({ index, style }) => {
    const customer = filteredCustomers[index];

    console.log("cust", customer);

    const isExpanded = selectedItem === index;

    return (
      <div
        style={{
          ...style,
          borderBottom: "1px solid #ccc",
          padding: "10px",
          backgroundColor: "#f9f9f9",
        }}
      >
        <div
          style={{
            display: "flex",
            padding: "5px",
            cursor: "pointer",
            fontWeight: "bold",
            alignItems: "center",
          }}
          onClick={() => setSelectedItem(isExpanded ? null : index)}
        >
          <div style={{ flex: 1, fontSize: "10px" }}>{index + 1}</div>
          <div style={{ flex: 2, fontSize: "10px" }}>{customer?.custName}</div>
          <div className="text-center" style={{ flex: 2, fontSize: "10px" }}>
            {customer?.custEmail || "-"}
          </div>
          <div className="text-center" style={{ flex: 2, fontSize: "10px" }}>
            {customer?.custMobile || "-"}
          </div>
          <div className="text-center" style={{ flex: 2, fontSize: "10px" }}>
            {customer?.custPhone || "-"}
          </div>

          <div className="text-center" style={{ flex: 2, fontSize: "10px" }}>
            {/* loyalist, potentialLoyalists etc */}
            <div
              className="text-center"
              style={{ flex: 2, fontSize: "10px", textTransform: "capitalize" }}
            >
              {customer?.rfmCategory || "-"}
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-3 ps-3">
            <ul className="mb-2 ps-3">
              <li>
                <strong>Transaction Count:</strong>{" "}
                {customer.transactions?.length}
              </li>
              <li>
                <strong>Total Sales:</strong> ₱
                {customer?.totalSpend.toLocaleString("en-PH", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </li>

              <li>
                <strong>Last Transaction:</strong>{" "}
                {new Date(customer.lastPurchaseDate).toLocaleDateString(
                  "en-US",
                  {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }
                )}
              </li>

              <li>
                <strong>Days Since Last Transaction:</strong>{" "}
                {customer.daysSinceLastPurchase}
              </li>
            </ul>

            <div className="d-flex mb-2">
              <Button
                className="btn btn-sm me-2 mt-2"
                style={{ backgroundColor: "#83c14a", color: "white" }}
                onClick={() => {
                  setModalCustomer(customer);
                  setShowModal(true);
                }}
              >
                Show Customer Transaction
              </Button>

              <Button
                className="btn btn-primary btn-sm me-2 mt-2"
                onClick={() => exportTransactionsToCsv(customer)}
              >
                Export Transactions
              </Button>
            </div>

            {expandedTransactions[index] &&
              customer.transactions?.map((t, i) => (
                <div className="card p-2 mb-2" key={i}>
                  <p>
                    <strong>Store:</strong> {t.storeSpend}
                  </p>
                  <p>
                    <strong>Date:</strong> {t.date.substring(0, 10)}
                  </p>
                  <p>
                    <strong>Transaction ID:</strong> {t.tranId}
                  </p>
                  <p>
                    <strong>Spend:</strong> ₱{t.totalSpend.toFixed(2)}
                  </p>
                </div>
              ))}
          </div>
        )}
      </div>
    );
  };

  console.log("fill", filteredCustomers);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-1 mt-4">
        <h2 className="fw-bold text-capitalize">
          <span style={{ color: "#83c14a" }}>Customer</span>{" "}
          <span style={{ color: "black" }}>Breakdown</span>
        </h2>
        <Export exportCsv={exportCsv} />
      </div>

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-sm-3">
          <select
            className="form-select"
            onChange={(e) => setSelectedStore(e.target.value)}
          >
            <option value="All">All Stores</option>
            {uniqueStores.map((s, i) => (
              <option key={i} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm-3">
          <select
            className="form-select"
            onChange={(e) => setSelectedFilter(e.target.value)}
          >
            <option value="All">All</option>
            <option value="loyalists">Loyalist</option>
            <option value="potentialLoyalists">Potential Loyalist</option>
            <option value="promising">PROMISING</option>
            <option value="hesitant">HESITANT</option>
            <option value="needAttention">NEED ATTENTION</option>
            <option value="detractors">DETRACTORS</option>
          </select>
        </div>
        <div className="col-sm-3">
          <input
            className="form-control"
            placeholder="Search Name"
            onChange={(e) => setNameSearch(e.target.value)}
          />
        </div>
        <div className="col-sm-3">
          <input
            className="form-control"
            placeholder="Search Mobile Number"
            onChange={(e) => setNumberSearch(e.target.value)}
          />
        </div>

        <div className="col-sm-3 mt-2">
          <select
            className="form-select"
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <option value="All">All Years</option>
            {uniqueYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p>
        <strong>Total:</strong> {filteredCustomers.length}
      </p>

      <div
        style={{
          display: "flex",
          backgroundColor: "#83c14a",
          color: "white",
          fontWeight: "bold",
          padding: "10px",
        }}
      >
        <div style={{ flex: 1 }}>#</div>
        <div style={{ flex: 2 }}>Customer</div>
        <div style={{ flex: 2 }}>Email</div>
        <div style={{ flex: 2 }}>Mobile</div>
        <div style={{ flex: 2 }}>Phone</div>
        <div style={{ flex: 1 }}>RFM</div>
      </div>

      {/* Virtualized List */}
      <List
        ref={listRef}
        height={600}
        itemCount={filteredCustomers.length}
        itemSize={getItemSize}
        width="100%"
      >
        {Row}
      </List>

      {showModal && modalCustomer && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Transactions for {modalCustomer.cust_name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {modalCustomer?.transactions?.length > 0 ? (
                  modalCustomer.transactions.map((t, i) => (
                    <div className="card p-3 mb-3" key={i}>
                      <p>
                        <strong>Store:</strong> {t.storeSpend}
                      </p>

                      <p>
                        <strong>Date:</strong>{" "}
                        {new Date(t.date).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>

                      <p>
                        <strong>Transaction ID:</strong> {t.tranId}
                      </p>
                      <p>
                        <strong>Spend:</strong> ₱
                        {Number(t.totalSpend).toLocaleString("en-PH", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    </div>
                  ))
                ) : (
                  <LoadingState />
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CustomerBreakdownPage;
